import { memo, useCallback } from 'react';

import { Scroll, Stack } from '@/components/layout';
import { createStyles, useScaling } from '@/hooks';
import PlayerFilterChip from './player-filter-chip';
import type { CalendarPlayer, CalendarViewMode } from './screen-utils';
import ViewToggle from './view-toggle';

export type CalendarControlsProps = {
  /** The guardian's players, straight from the account. */
  players: CalendarPlayer[];
  /** Active player id; `null` means every player is shown (default). */
  selectedPlayer: string | null;
  onSelectPlayer: (playerId: string | null) => void;
  mode: CalendarViewMode;
  onChangeMode: (mode: CalendarViewMode) => void;
};

/**
 * Player filter chips + the Calendar/List view toggle shown under the screen header.
 *
 * The chip scroll pads its content on every side so each chip's shadow can spread instead
 * of being clipped by the horizontal scroll bounds; the screen padding lives here (on the
 * scroll content and the toggle group) rather than on the row, for the same reason.
 */
function CalendarControls({
  players,
  selectedPlayer,
  onSelectPlayer,
  mode,
  onChangeMode,
}: CalendarControlsProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  // Tap the active chip again to clear the filter back to "all players".
  const handleSelectPlayer = useCallback(
    (playerId: string) => onSelectPlayer(selectedPlayer === playerId ? null : playerId),
    [onSelectPlayer, selectedPlayer],
  );

  return (
    <Stack direction="row" align="center" justify="space-between" paddingBottom={scale(4)}>
      <Stack flex={1}>
        <Scroll
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipRow}>
          {players.map(player => (
            <PlayerFilterChip
              key={player.id}
              player={player}
              isActive={selectedPlayer === player.id}
              onPress={handleSelectPlayer}
            />
          ))}
        </Scroll>
      </Stack>

      <Stack paddingLeft={scale(8)} paddingRight={scale(20)}>
        <ViewToggle mode={mode} onChangeMode={onChangeMode} />
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  chipRow: {
    alignItems: 'center',
    gap: scale(10),
    paddingLeft: scale(20),
    paddingRight: scale(12),
    paddingVertical: scale(12),
  },
}));

export default memo(CalendarControls);
